import React, { useState } from 'react';
import {
    EuiPanel,
    EuiText,
    EuiFlexGroup,
    EuiFlexItem,
    EuiSwitch,
    EuiSpacer,
    EuiIcon,
    EuiImage,
    EuiBadge,
    EuiToolTip,
} from '@elastic/eui';
import {
    substituteVariables,
    getDummyVariables,
    extractVariablesFromText,
    getCharacterCountInfo,
} from './variableHelper';

export const NotificationPreview = ({ title, description, image, language, targetAudience, selectedOutletCount, totalOutletCount }) => {
    const [showFallback, setShowFallback] = useState(false);

    const TITLE_LIMIT = 65;
    const DESCRIPTION_LIMIT = 240;

    const processedTitle = substituteVariables(
        title,
        showFallback ? {} : getDummyVariables(),
        showFallback
    );

    const processedDescription = substituteVariables(
        description,
        showFallback ? {} : getDummyVariables(),
        showFallback
    );

    const titleCharInfo = getCharacterCountInfo(title, TITLE_LIMIT);
    const descCharInfo = getCharacterCountInfo(description, DESCRIPTION_LIMIT);

    const usedVariables = [
        ...extractVariablesFromText(title),
        ...extractVariablesFromText(description),
    ];

    const uniqueVariables = usedVariables.filter(
        (v, index, self) => index === self.findIndex((t) => t.key === v.key)
    );

    return (
        <EuiPanel paddingSize="m" style={{ maxWidth: 600 }}>
            <EuiFlexGroup justifyContent="spaceBetween" alignItems="center">
                <EuiFlexItem grow={false}>
                    <EuiText size="s">
                        <h4>Notification Preview</h4>
                    </EuiText>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiSwitch
                        label="Show with fallback values"
                        checked={showFallback}
                        onChange={(e) => setShowFallback(e.target.checked)}
                    />
                </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size="m" />
            
            {targetAudience && (
                <>
                    <EuiFlexGroup alignItems="center" gutterSize="s">
                        <EuiFlexItem grow={false}>
                            <EuiIcon type="users" size="s" color="subdued" />
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiText size="xs" color="subdued">
                                Target: {targetAudience === 'all' 
                                    ? 'All outlets' 
                                    : `${selectedOutletCount || 0} of ${totalOutletCount || 0} outlets selected`
                                }
                            </EuiText>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                    <EuiSpacer size="s" />
                </>
            )}

            <div
                style={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: '12px',
                    padding: '16px',
                    position: 'relative',
                    minHeight: '120px',
                }}
            >
                <div
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                >
                    <EuiFlexGroup alignItems="center" gutterSize="s">
                        <EuiFlexItem grow={false}>
                            <EuiIcon type="bell" size="m" color="primary" />
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiText size="xs" color="subdued">
                                {language?.label || 'Default Language'}
                            </EuiText>
                        </EuiFlexItem>
                    </EuiFlexGroup>

                    <EuiSpacer size="xs" />

                    <EuiText size="m">
                        <strong>{processedTitle || 'Notification Title'}</strong>
                    </EuiText>

                    {!titleCharInfo.exceedsLimit && (
                        <EuiToolTip content={`Title may exceed ${TITLE_LIMIT} characters with longest possible values`}>
                            <EuiBadge color="warning" iconType="alert">
                                Character limit warning
                            </EuiBadge>
                        </EuiToolTip>
                    )}

                    <EuiSpacer size="xs" />

                    <EuiText size="s" color="subdued">
                        {processedDescription || 'Notification description will appear here'}
                    </EuiText>

                    {descCharInfo.exceedsLimit && (
                        <EuiToolTip content={`Description may exceed ${DESCRIPTION_LIMIT} characters with longest possible values`}>
                            <EuiBadge color="warning" iconType="alert">
                                Character limit warning
                            </EuiBadge>
                        </EuiToolTip>
                    )}

                    {image && image.src && (
                        <>
                            <EuiSpacer size="s" />
                            <EuiImage
                                size="m"
                                hasShadow
                                alt="Notification image"
                                src={image.src}
                                style={{ maxHeight: '150px', borderRadius: '4px' }}
                            />
                        </>
                    )}

                    <EuiSpacer size="s" />

                    <EuiText size="xs" color="subdued" textAlign="right">
                        now
                    </EuiText>
                </div>
            </div>

            <EuiSpacer size="s" />

            {uniqueVariables.length > 0 && (
                <>
                    <EuiText size="xs">
                        <strong>Variables used:</strong>
                    </EuiText>
                    <EuiSpacer size="xs" />
                    <EuiFlexGroup gutterSize="xs" wrap>
                        {uniqueVariables.map((variable) => (
                            <EuiFlexItem key={variable.key} grow={false}>
                                <EuiToolTip
                                    content={
                                        showFallback
                                            ? `Fallback: "${variable.fallback}"`
                                            : `Sample: "${getDummyVariables()[variable.key]}"`
                                    }
                                >
                                    <EuiBadge color="hollow">{variable.label}</EuiBadge>
                                </EuiToolTip>
                            </EuiFlexItem>
                        ))}
                    </EuiFlexGroup>
                </>
            )}

            <EuiSpacer size="s" />

            <EuiFlexGroup gutterSize="xs">
                <EuiFlexItem grow={false}>
                    <EuiText size="xs" color="subdued">
                        <strong>Title:</strong> {titleCharInfo.current}/{TITLE_LIMIT} chars
                        {titleCharInfo.exceedsLimit && ` (max possible: ${titleCharInfo.maxPossible})`}
                    </EuiText>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiText size="xs" color="subdued">
                        <strong>Description:</strong> {descCharInfo.current}/{DESCRIPTION_LIMIT} chars
                        {descCharInfo.exceedsLimit && ` (max possible: ${descCharInfo.maxPossible})`}
                    </EuiText>
                </EuiFlexItem>
            </EuiFlexGroup>
        </EuiPanel>
    );
};